import { ActionTree, MutationTree, GetterTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import numeral from "numeral";
import ru from "apexcharts/dist/locales/ru.json";
import cloneDeep from "lodash/cloneDeep";

export const CHART_DATA = "CLIENT_DETAILS_CHART_DATA";

export type ClientChartMetric = "shipments" | "prices" | "transport";

export const CLIENT_CHART_METRICS: ClientChartMetric[] = [
  "shipments",
  "prices",
  "transport"
];
export type ClientChartDisplayType = "line" | "bar" | "area";

export interface ClientChartsState {
  loading: boolean;
  loaded: boolean;
  fatalError: boolean;
  /** Что показываем: отгрузки / цена / транспорт */
  activeMetric: ClientChartMetric;
  /** Вид диаграммы: линия / столбцы / область */
  chartType: ClientChartDisplayType;
  targetDates: Date[];
  product: any;
  productContainer: any;
  lastRaw: any | null;
  shipmentsSeries: any[];
  shipmentsOptions: any;
  pricesSeries: any[];
  pricesOptions: any;
  transportSeries: any[];
  transportOptions: any;
}

/** Как Vue-фильтр `numFormat` в `main.ts`: округление + numeral. */
const CHART_VALUE_NUMERAL_FORMAT = "0,0.00";

function formatChartNumericValue(val: any): string {
  if (val == null || val === "") {
    return "";
  }
  const n = typeof val === "number" ? val : parseFloat(String(val));
  if (isNaN(n)) {
    return "";
  }
  const rounded = Math.round(n * 10000) / 10000;
  return numeral(rounded).format(CHART_VALUE_NUMERAL_FORMAT);
}

const defaultOptionsBase: any = {
  chart: {
    locales: [ru],
    defaultLocale: "ru",
    toolbar: { show: true },
    zoom: { enabled: true }
  },
  dataLabels: { enabled: false },
  stroke: { width: 2, curve: "smooth" },
  xaxis: { type: "category" },
  tooltip: {
    shared: true,
    intersect: false,
    y: {
      formatter(value: any) {
        if (value == null || value === "") {
          return "—";
        }
        const s = formatChartNumericValue(value);
        return s === "" ? "—" : s;
      }
    }
  }
};

function toClientDocId(id: any): string {
  if (id == null) return "";
  if (typeof id === "string" && id.indexOf("d:") === 0) return id;
  return `d:clients:${id}`;
}

/** b-combobox may store full doc ref string or { id } depending on schema */
function normalizeDocRef(v: any): any {
  if (v == null) return null;
  if (typeof v === "object" && v.id != null) return v.id;
  return v;
}

function periodLabel(p: string): string {
  return moment(p).format("MMM YYYY");
}

function buildCategoriesFromRows(
  rows: { period: string }[],
  extra: string[] = []
): string[] {
  const set = new Set<string>();
  rows.forEach(r => {
    if (r.period) set.add(String(r.period).slice(0, 10));
  });
  extra.forEach(e => set.add(e));
  return Array.from(set).sort();
}

function buildStackedSeries(
  rows: any[],
  categories: string[],
  valueField: string,
  seriesName: (row: any) => string
): { series: any[]; names: string[] } {
  const byKey: { [k: string]: { [cat: string]: number } } = {};
  rows.forEach(r => {
    const p = String(r.period).slice(0, 10);
    const key = seriesName(r);
    if (!byKey[key]) byKey[key] = {};
    const v = parseFloat(r[valueField]);
    byKey[key][p] = (byKey[key][p] || 0) + (isNaN(v) ? 0 : v);
  });
  const series = Object.keys(byKey).map(name => ({
    name,
    data: categories.map(c => (byKey[name][c] != null ? byKey[name][c] : null))
  }));
  return { series, names: Object.keys(byKey) };
}

function buildChartOptions(
  chartType: ClientChartDisplayType,
  categories: string[],
  yTitle: string,
  title: string
): any {
  const opts: any = cloneDeep(defaultOptionsBase);
  opts.chart.type = chartType === "area" ? "area" : chartType;
  if (chartType === "area") {
    opts.stroke = { curve: "smooth", width: 2 };
    opts.fill = { type: "gradient", opacity: [0.35, 0.05] };
  }
  opts.title = { text: title, align: "left" };
  opts.xaxis = { ...opts.xaxis, categories: categories.map(periodLabel) };
  opts.yaxis = {
    title: { text: yTitle },
    labels: {
      formatter(val: any) {
        return formatChartNumericValue(val);
      }
    }
  };
  return opts;
}

function buildFilter(params: {
  clientId: any;
  targetDates: Date[];
  product: any;
  productContainer: any;
}) {
  const q: any = {
    client_id: toClientDocId(params.clientId),
    date_from: moment(params.targetDates[0]).format("YYYY-MM-DD"),
    date_to: moment(params.targetDates[1]).format("YYYY-MM-DD")
  };
  const pid = normalizeDocRef(params.product);
  const pcid = normalizeDocRef(params.productContainer);
  if (pid) {
    q.product_id = pid;
  }
  if (pcid) {
    q.product_container_id = pcid;
  }
  return q;
}

function rebuildChartsFromRaw(s: ClientChartsState) {
  if (!s.lastRaw) {
    return;
  }
  const content = s.lastRaw;
  const shipments = content.shipments || [];
  const pricesRaw = content.prices || [];
  const transport = content.transport || [];

  const prices = pricesRaw.map((r: any) => {
    const w = parseFloat(r.weight_sum);
    const c = parseFloat(r.cost_sum);
    const price = w > 0 ? c / w : 0;
    return {
      ...r,
      price_per_kg: price
    };
  });

  const shipCats = buildCategoriesFromRows(shipments);
  const priceCats = buildCategoriesFromRows(prices);
  const transCats = buildCategoriesFromRows(
    transport.map((t: any) => ({ period: t.period }))
  );
  const categories = buildCategoriesFromRows([...shipments, ...prices], transCats);

  const shipGrouped = buildStackedSeries(
    shipments,
    shipCats.length ? shipCats : categories,
    "kg",
    (r: any) => `${r.product_name || ""} / ${r.product_container_name || ""}`
  );

  const priceGrouped = buildStackedSeries(
    prices,
    priceCats.length ? priceCats : categories,
    "price_per_kg",
    (r: any) => `${r.product_name || ""} / ${r.product_container_name || ""}`
  );

  let transportCategories = transCats.length
    ? transCats
    : buildCategoriesFromRows(transport.map((t: any) => ({ period: t.period })));
  if (!transportCategories.length) {
    transportCategories =
      shipCats.length > 0 ? shipCats : priceCats.length > 0 ? priceCats : categories;
  }

  const transportByMonth: { [k: string]: number } = {};
  transport.forEach((t: any) => {
    const p = String(t.period).slice(0, 10);
    transportByMonth[p] = parseFloat(t.cost_rub) || 0;
  });

  const transportSeries = [
    {
      name: "Транспорт, руб.",
      data: transportCategories.map(
        c => (transportByMonth[c] != null ? transportByMonth[c] : null)
      )
    }
  ];

  const ct = s.chartType;

  s.shipmentsSeries = shipGrouped.series;
  s.shipmentsOptions = buildChartOptions(
    ct,
    shipCats.length ? shipCats : categories,
    "кг",
    "Отгрузки по номенклатурам, кг"
  );
  s.pricesSeries = priceGrouped.series;
  s.pricesOptions = buildChartOptions(
    ct,
    priceCats.length ? priceCats : categories,
    "руб./кг",
    "Цена реализации, руб./кг"
  );
  s.transportSeries = transportSeries;
  s.transportOptions = buildChartOptions(
    ct,
    transportCategories,
    "руб.",
    "Транспортные расходы, руб."
  );
}

const state: ClientChartsState = {
  loading: false,
  loaded: false,
  fatalError: false,
  activeMetric: "shipments",
  chartType: "line",
  targetDates: [
    moment()
      .subtract(5, "months")
      .startOf("month")
      .toDate(),
    moment()
      .endOf("month")
      .toDate()
  ],
  product: null,
  productContainer: null,
  lastRaw: null,
  shipmentsSeries: [],
  shipmentsOptions: {},
  pricesSeries: [],
  pricesOptions: {},
  transportSeries: [],
  transportOptions: {}
};

const mutations: MutationTree<ClientChartsState> = {
  START_LOADING(s) {
    s.loading = true;
    s.fatalError = false;
  },
  LOADED_SUCCESS(s) {
    s.loading = false;
    s.loaded = true;
  },
  LOAD_FAILED(s) {
    s.loading = false;
    s.fatalError = true;
  },
  SET_ACTIVE_METRIC(s, m: ClientChartMetric) {
    s.activeMetric = m;
  },
  SET_CHART_TYPE(s, t: ClientChartDisplayType) {
    s.chartType = t;
    rebuildChartsFromRaw(s);
  },
  SET_TARGET_DATES(s, d: Date[]) {
    s.targetDates = d;
  },
  SET_PRODUCT(s, p: any) {
    s.product = p;
  },
  SET_PRODUCT_CONTAINER(s, p: any) {
    s.productContainer = p;
  },
  SET_LAST_RAW(s, raw: any) {
    s.lastRaw = raw;
    rebuildChartsFromRaw(s);
  }
};

const actions: ActionTree<ClientChartsState, RootState> = {
  async loadChartData({ commit, state }, clientId: any) {
    const vm = (this as any)._vm;
    if (!clientId || clientId === "new") {
      return;
    }
    commit("START_LOADING");
    const filter = buildFilter({
      clientId,
      targetDates: state.targetDates,
      product: state.product,
      productContainer: state.productContainer
    });

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[client-charts-store] fetch_start", filter);
    }

    try {
      const result: ServerResult = await vm.$db([
        vm.$q(CHART_DATA).raw("reports.client_details_charts").action(filter)
      ]);
      const content = result.getOperation(CHART_DATA).getContent();
      commit("SET_LAST_RAW", content);

      commit("LOADED_SUCCESS");

      if (process.env.NODE_ENV !== "production") {
        const shipments = content.shipments || [];
        const prices = content.prices || [];
        const transport = content.transport || [];
        // eslint-disable-next-line no-console
        console.debug("[client-charts-store] fetch_success", {
          shipments: shipments.length,
          prices: prices.length,
          transport: transport.length
        });
      }
    } catch (e) {
      commit("LOAD_FAILED");
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[client-charts-store] fetch_error", e);
      }
    }
  },

};

const getters: GetterTree<ClientChartsState, RootState> = {};

/** Без префикса `$_`: Vuex нормализует namespace и `mapState` с `$_…` даёт «module not found». */
export const CLIENT_CHARTS_STORE_KEY = "clientDetailsCharts";

export const clientChartsStore = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
